import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Sparkles, Newspaper } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CATEGORY_META, renderMarkdown } from '../pages/NewsPage';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Bell icon with red dot when unread. Click → navigates to /news.
 * Light-touch polling every 5 minutes; also re-fetches when window regains focus.
 */
export const NewsBell = ({ textClass = 'text-stone-600' }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/news/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCount(res.data.count || 0);
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [user, fetchCount]);

  if (!user) return null;

  return (
    <button
      onClick={() => navigate('/news')}
      className={`relative p-2 rounded-lg hover:bg-stone-100 ${textClass}`}
      title="What's new"
      data-testid="news-bell"
    >
      <Bell className="w-5 h-5" />
      {count > 0 && (
        <span
          className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"
          data-testid="news-bell-dot"
        />
      )}
    </button>
  );
};

/**
 * Full-width banner shown above the Dashboard when there's a new unread post.
 * Auto-dismissed for the user once they click "View" or X (calls mark-read).
 */
export const NewsBanner = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [latest, setLatest] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    axios.get(`${API}/api/news/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (cancelled) return;
      if (res.data.count > 0 && res.data.latest) {
        setLatest(res.data.latest);
      }
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [token, user]);

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await axios.post(`${API}/api/news/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* silent */ }
  };

  if (!latest || dismissed) return null;

  const meta = CATEGORY_META[latest.category] || CATEGORY_META.new_feature;
  const Icon = meta.icon;

  return (
    <div
      className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-4 sm:p-5 text-white shadow-md mb-6"
      data-testid="news-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
            <span className="text-[10px] opacity-80">What's new</span>
          </div>
          <h3 className="font-nunito font-bold text-lg leading-tight">{latest.title}</h3>
          <p
            className="text-sm opacity-90 mt-1 line-clamp-2"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(renderMarkdown(latest.body), {
                ALLOWED_TAGS: ['strong', 'em', 'br'],
                ALLOWED_ATTR: [],
              }),
            }}
          />
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={() => { handleDismiss(); navigate('/news'); }}
              className="bg-white text-purple-700 hover:bg-white/90 font-semibold text-sm px-4 py-1.5 rounded-full flex items-center gap-1"
              data-testid="news-banner-view"
            >
              <Newspaper className="w-3.5 h-3.5" /> View update
            </button>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white text-xs underline"
              data-testid="news-banner-dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/80 hover:text-white p-1 -mt-1 -mr-1"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
